'use client';

import { initKyHttp as initKyHttpNest } from './nest/api';
import { setupZeusNestInterceptors } from './nest/setup';
import { initKyHttp as initKyHttpV2 } from './v2/api';
import { setupZeusV2Interceptors } from './v2/setup';
import { useCommonAuthProvider } from '@modules/auth/providers/CommonAuthProvider/CommonAuthProvider';

import React, { FC, useEffect, useRef } from 'react';

/**
 * Bridges the CommonAuthProvider context into the shared Zeus ky client.
 *
 * Must be placed inside CommonAuthProvider in the component tree. Registers
 * auth once (via the empty-deps effect) while keeping auth functions always
 * current through a ref updated on each render.
 */
export const ZeusClientSetupProvider: FC<React.PropsWithChildren> = ({ children }) => {
  const { getAccessToken, loginWithRedirect } = useCommonAuthProvider();

  const authRef = useRef({ getAccessToken, loginWithRedirect });

  // Keep ref current on every render so hooks always call the latest auth functions.
  useEffect(() => {
    authRef.current = { getAccessToken, loginWithRedirect };
  }, [getAccessToken, loginWithRedirect]);

  useEffect(() => {
    const cleanupNest = setupZeusNestInterceptors(authRef);
    const cleanupV2 = setupZeusV2Interceptors(authRef);
    initKyHttpNest();
    initKyHttpV2();

    return () => {
      cleanupNest();
      cleanupV2();
    };
  }, []);

  return children;
};
